// Decorative traffic of H0: the mockup's vehicles going back and forth along their routes.
// This is ambience for measuring the animation frame cap, not the route simulation of later milestones.
import { SEA_Y, topAt, type Island, type RouteKind } from './island';
import { along, measurePath, pingPong, routePath, type MeasuredPath, type PathPose } from './paths';

export type VehicleKind = 'truck' | 'loco' | 'wagon' | 'barge' | 'boat' | 'drone';

export interface VehiclePose {
  readonly kind: VehicleKind;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly angle: number;
}

/** Cruise speed per route kind, in tiles per second: slow and calm. */
const SPEED: Record<RouteKind, number> = { road: 0.3, rail: 0.28, barge: 0.18, boat: 0.22, drone: 0.4 };

/** Path fraction between the locomotive and each wagon (as in the mockup). */
const WAGON_GAPS = [0.04, 0.08] as const;

export interface TrafficRoute {
  readonly kind: RouteKind;
  readonly path: MeasuredPath;
  /** Seconds to go from one end to the other. */
  readonly duration: number;
  /** Fraction of the path kept behind the lead vehicle for its wagons. */
  readonly span: number;
  /** Ping-pong phase at time zero, so that time zero reproduces the mockup. */
  readonly phase0: number;
}

export function planTraffic(island: Island): TrafficRoute[] {
  return island.routes.map((route) => {
    const path = measurePath(routePath(island, route));
    const span = route.kind === 'rail' ? WAGON_GAPS[WAGON_GAPS.length - 1] ?? 0 : 0;
    return {
      kind: route.kind,
      path,
      duration: path.length / SPEED[route.kind],
      span,
      phase0: Math.min(1, Math.max(0, (route.at - span) / (1 - span))),
    };
  });
}

/** Number of vehicles (counting each wagon) that one copy of the traffic draws. */
export function vehiclesPerCopy(routes: readonly TrafficRoute[]): number {
  return routes.reduce((n, r) => n + (r.kind === 'rail' ? 1 + WAGON_GAPS.length : 1), 0);
}

/** Receives one vehicle pose; called in a fixed order (route, copy, then loco before wagons). */
export type PoseVisitor = (kind: VehicleKind, x: number, y: number, z: number, angle: number) => void;

/**
 * Visits the pose of every vehicle at `seconds` without allocating. With `copies` above one, each
 * route carries that many consists spread evenly over its back-and-forth cycle (the stress mode).
 */
export function forEachTrafficPose(island: Island, routes: readonly TrafficRoute[], seconds: number, copies: number, visit: PoseVisitor): void {
  for (const route of routes) {
    for (let k = 0; k < copies; k++) {
      const offset = route.phase0 + (2 * k) / copies;
      const p = pingPong(seconds, route.duration, offset);
      const goingBack = pingPong(seconds + 1e-3, route.duration, offset) < p;
      const lead = route.span + p * (1 - route.span);
      if (route.kind === 'rail') {
        visitPose(island, route, 'loco', lead, false, visit);
        for (const gap of WAGON_GAPS) visitPose(island, route, 'wagon', lead - gap, false, visit);
      } else {
        const kind: VehicleKind = route.kind === 'road' ? 'truck' : route.kind;
        visitPose(island, route, kind, lead, goingBack, visit);
      }
    }
  }
}

/** Poses of every vehicle at `seconds`, as a list (see forEachTrafficPose). */
export function trafficPoses(island: Island, routes: readonly TrafficRoute[], seconds: number, copies = 1): VehiclePose[] {
  const poses: VehiclePose[] = [];
  forEachTrafficPose(island, routes, seconds, copies, (kind, x, y, z, angle) => {
    poses.push({ kind, x, y, z, angle });
  });
  return poses;
}

const scratch: PathPose = { x: 0, z: 0, angle: 0 };

function visitPose(island: Island, route: TrafficRoute, kind: VehicleKind, fraction: number, reversed: boolean, visit: PoseVisitor): void {
  const q = along(route.path, fraction, scratch);
  let y: number;
  if (route.kind === 'boat') y = SEA_Y;
  else if (route.kind === 'barge') y = -0.12;
  else if (route.kind === 'drone') y = 0.75 + Math.sin(fraction * Math.PI) * 0.35;
  else y = Math.max(topAt(island, q.x, q.z), 0) + 0.032;
  visit(kind, q.x, y, q.z, reversed ? q.angle + Math.PI : q.angle);
}
